// Matomo Analytics Service
// Site ID: 8
// Server: stats.host-on.de
// 
// Aktivierung über ENV: REACT_APP_MATOMO_ENABLED=true
// In der Testumgebung deaktiviert, nur in Produktion aktiv.

const MATOMO_URL = 'https://stats.host-on.de';
const MATOMO_SITE_ID = 8;

// Check if Matomo should be enabled via ENV
const MATOMO_ENABLED = process.env.REACT_APP_MATOMO_ENABLED === 'true';

let matomoInitialized = false;
let matomoQueue = [];

// Initialize Matomo tracking
export function initMatomo() {
  // Skip if disabled via ENV
  if (!MATOMO_ENABLED) {
    console.log('[Matomo] Tracking disabled via REACT_APP_MATOMO_ENABLED');
    return;
  }
  
  if (matomoInitialized) return;
  if (typeof window === 'undefined') return;

  // Create _paq array if it doesn't exist
  window._paq = window._paq || [];

  // Matomo configuration
  window._paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
  window._paq.push(['setSiteId', MATOMO_SITE_ID]);
  
  // Enable link tracking
  window._paq.push(['enableLinkTracking']);
  
  // Enable heart beat timer (for accurate time on page)
  window._paq.push(['enableHeartBeatTimer']);
  
  // Respect Do Not Track browser setting
  window._paq.push(['setDoNotTrack', true]);
  
  // Cookie domain
  window._paq.push(['setCookieDomain', '*.wolkenhelden.de']);
  
  // Domains to track
  window._paq.push(['setDomains', ['*.wolkenhelden.de']]);

  // Load the Matomo script
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `${MATOMO_URL}/matomo.js`;
  
  script.onload = () => {
    matomoInitialized = true;
    // Process queued events
    matomoQueue.forEach(fn => fn());
    matomoQueue = [];
  };

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(script, firstScript);
}

// Disable Matomo tracking
export function disableMatomo() {
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  window._paq.push(['optUserOut']);
  
  // Remove Matomo cookies
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_pk_') || name.startsWith('mtm_') || name.startsWith('matomo_')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
}

// Queue function to ensure Matomo is loaded
function queueOrExecute(fn) {
  if (matomoInitialized && window._paq) {
    fn();
  } else {
    matomoQueue.push(fn);
  }
}

// Track page view
export function trackPageView(title, url) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    if (url) {
      window._paq.push(['setCustomUrl', url]);
    }
    if (title) {
      window._paq.push(['setDocumentTitle', title]);
    }
    window._paq.push(['trackPageView']);
  });
}

// Track custom event
export function trackEvent(category, action, name, value) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['trackEvent', category, action, name, value]);
  });
}

// Track site search
export function trackSiteSearch(keyword, category, resultsCount) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['trackSiteSearch', keyword, category, resultsCount]);
  });
}

// ============================================
// E-Commerce Tracking Functions
// ============================================

// Set E-Commerce view (product page)
export function setEcommerceView(productSku, productName, categoryName, price) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['setEcommerceView', productSku, productName, categoryName, price]);
    window._paq.push(['trackPageView']);
  });
}

// Add item to cart (or update quantity)
export function addEcommerceItem(productSku, productName, categoryName, price, quantity) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['addEcommerceItem', productSku, productName, categoryName, price, quantity]);
  });
}

// Remove item from cart
export function removeEcommerceItem(productSku) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['removeEcommerceItem', productSku]);
  });
}

// Clear cart
export function clearEcommerceCart() {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['clearEcommerceCart']);
  });
}

// Track cart update
export function trackEcommerceCartUpdate(grandTotal) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['trackEcommerceCartUpdate', grandTotal]);
  });
}

// Track order / purchase
export function trackEcommerceOrder(
  orderId,
  grandTotal,
  subTotal,
  tax,
  shipping,
  discount
) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push([
      'trackEcommerceOrder',
      orderId,
      grandTotal,
      subTotal,
      tax,
      shipping,
      discount || false
    ]);
  });
}

// ============================================
// Goal Tracking
// ============================================

// Track a goal
export function trackGoal(goalId, customRevenue) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    if (customRevenue !== undefined) {
      window._paq.push(['trackGoal', goalId, customRevenue]);
    } else {
      window._paq.push(['trackGoal', goalId]);
    }
  });
}

// ============================================
// User Identification
// ============================================

// Set user ID for logged-in users
export function setUserId(userId) {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['setUserId', userId]);
  });
}

// Reset user ID on logout
export function resetUserId() {
  if (!MATOMO_ENABLED) return;
  if (typeof window === 'undefined') return;
  
  window._paq = window._paq || [];
  
  queueOrExecute(() => {
    window._paq.push(['resetUserId']);
    window._paq.push(['appendToTrackingUrl', 'new_visit=1']);
    window._paq.push(['trackPageView']);
    window._paq.push(['appendToTrackingUrl', '']);
  });
}

// Check if Matomo is enabled
export function isMatomoEnabled() {
  return MATOMO_ENABLED;
}

export default {
  isMatomoEnabled,
  initMatomo,
  disableMatomo,
  trackPageView,
  trackEvent,
  trackSiteSearch,
  setEcommerceView,
  addEcommerceItem,
  removeEcommerceItem,
  clearEcommerceCart,
  trackEcommerceCartUpdate,
  trackEcommerceOrder,
  trackGoal,
  setUserId,
  resetUserId
};
